import type { ReportElementType } from '@highimpact-frontend/api-types';

import {
  flattenExportableElements,
  legacyDomExportElement,
} from '../../report/legacyDomExport';
import { resolveReportExportDisplayName } from './resolveReportExportDisplayName';
import type {
  ReportExportMode,
  ReportGatherOptions,
  ReportWidgetExportResult,
  WidgetInstanceCapabilities,
  WidgetInstanceRegistry,
} from './types';

/** Create a widget instance registry that centralizes the capabilities of all widgets. */
export function createWidgetInstanceRegistry(
  onChange?: () => void
): WidgetInstanceRegistry {
  const byElementId = new Map<number, WidgetInstanceCapabilities>();
  const byWidgetId = new Map<string, WidgetInstanceCapabilities>();

  const notify = () => onChange?.();

  /** Register a widget instance. */
  const registerInstance = (capabilities: WidgetInstanceCapabilities): void => {
    byElementId.set(capabilities.dashboardElementId, capabilities);
    byWidgetId.set(capabilities.widgetId, capabilities);
    notify();
  };

  /** Unregister a widget instance. */
  const unregisterInstance = (dashboardElementId: number): void => {
    const existing = byElementId.get(dashboardElementId);
    if (existing) {
      byWidgetId.delete(existing.widgetId);
    }
    byElementId.delete(dashboardElementId);
    notify();
  };

  /** Get a widget instance by dashboard element id (which is unique across all dashboards) */
  const getInstance = (
    dashboardElementId: number
  ): WidgetInstanceCapabilities | undefined => byElementId.get(dashboardElementId);

  /** Get a widget instance by widget id (unicity is guaranteed only within a single dashboard) */
  const getInstanceByWidgetId = (
    widgetId: string
  ): WidgetInstanceCapabilities | undefined => byWidgetId.get(widgetId);

  /** Check if at least one report element's expected export format
   * corresponds to an interactive export format in the widget instance */
  const hasInteractiveReportExportsForElements = (
    elements: ReportElementType[]
  ): boolean => {
    const leaves = flattenExportableElements(elements);

    return leaves.some((element) => {
      if (element.element_type === 'collection') {
        return false;
      }

      const caps = getInstanceByWidgetId(element.template_variable);
      const reportExport = caps?.reportExports?.find(
        (exp) => exp.format === element.export_type
      );

      return reportExport?.supportsInteractive === true;
    });
  };

  /** Gather exported widget content for a list of report elements to be included in a report. */
  const gatherReportExports = async (
    elements: ReportElementType[],
    mode: ReportExportMode,
    options: ReportGatherOptions = {}
  ): Promise<ReportWidgetExportResult[]> => {
    const leaves = flattenExportableElements(elements);
    const totalCount = leaves.length;
    const resolveDisplayName =
      options.resolveDisplayName ??
      ((element) => resolveReportExportDisplayName(element, { getWidgetByTemplateVariable: () => undefined }));
    const results: ReportWidgetExportResult[] = [];
    let completedCount = 0;

    const emitGatheringProgress = (
      currentElement?: ReportElementType,
      currentDisplayName?: string
    ) => {
      options.onProgress?.({
        phase: 'gathering',
        mode,
        completedCount,
        totalCount,
        currentElement,
        currentDisplayName,
      });
    };

    /** Export the widget content for a single report element.
     * Uses recursion if the element is a collection.
     */
    const exportLeaf = async (
      element: ReportElementType
    ): Promise<ReportWidgetExportResult> => {
      const caps = getInstanceByWidgetId(element.template_variable);
      const exportType =
        element.element_type === 'collection' ? undefined : element.export_type;
      const reportExport = caps?.reportExports?.find(
        (exp) => exp.format === exportType
      );

      let result: string | null | undefined;

      if (reportExport) {
        result = await reportExport.export({ mode, signal: options.signal });
      } else {
        result = await legacyDomExportElement(
          element,
          options.legacyExportContext ?? {}
        );
      }

      if (result === null && mode === 'interactive' && options.onExportCancelled) {
        const displayName = resolveDisplayName(element);
        const decision = await options.onExportCancelled(element, displayName);
        if (decision === 'abort') {
          throw new Error('Report export cancelled by user');
        }
      }

      return { element, result: result ?? undefined };
    };

    try {
      if (mode === 'headless') {
        emitGatheringProgress();
        const settled = await Promise.all(
          leaves.map(async (element) => {
            const row = await exportLeaf(element);
            completedCount += 1;
            emitGatheringProgress();
            return row;
          })
        );
        results.push(...settled);
      } else {
        for (let index = 0; index < leaves.length; index += 1) {
          const element = leaves[index];
          const displayName = resolveDisplayName(element);
          emitGatheringProgress(element, displayName);
          const row = await exportLeaf(element);
          results.push(row);
          completedCount += 1;
          emitGatheringProgress();
        }
      }

      options.onProgress?.({ phase: 'done' });
      return results;
    } catch (error) {
      options.onProgress?.({ phase: 'error', error });
      throw error;
    }
  };

  /** Consumer-facing API */
  return {
    registerInstance,
    unregisterInstance,
    getInstance,
    getInstanceByWidgetId,
    gatherReportExports,
    hasInteractiveReportExportsForElements,
  };
}
